import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import {
  Brain, CheckCircle, XCircle, FileText, Shield, AlertTriangle,
  Search, GitCompare, Languages, Download, MessageCircle, BarChart3,
  Workflow, Scan, Clock, Lightbulb
} from 'lucide-react';
import IntelligenceAnalyzer from '../components/enterprise/IntelligenceAnalyzer';
import CompliancePanel from '../components/enterprise/CompliancePanel';
import SearchQAPanel from '../components/enterprise/SearchQAPanel';
import ToolsPanel from '../components/enterprise/ToolsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EnterpriseIntelligence = () => {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    document.title = 'ReasonDMS | Enterprise Intelligence';
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/enterprise/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setConfig(await response.json());
    } catch (err) {
      console.error('Failed to load enterprise config:', err);
    }
  };

  const components = [
    { key: 'document_summarization', label: 'Summarization', icon: FileText },
    { key: 'document_qa', label: 'Document QA', icon: MessageCircle },
    { key: 'compliance_engine', label: 'Compliance', icon: Shield },
    { key: 'risk_scoring', label: 'Risk Scoring', icon: AlertTriangle },
    { key: 'version_comparison', label: 'Version Compare', icon: GitCompare },
    { key: 'fraud_detection', label: 'Fraud Detection', icon: Scan },
    { key: 'intelligent_redaction', label: 'Redaction', icon: Shield },
    { key: 'quality_scoring', label: 'Quality Score', icon: BarChart3 },
    { key: 'relationship_graph', label: 'Relationships', icon: Brain },
    { key: 'semantic_search', label: 'Semantic Search', icon: Search },
    { key: 'workflow_suggestion', label: 'Workflow AI', icon: Workflow },
    { key: 'lifecycle_prediction', label: 'Lifecycle', icon: Clock },
    { key: 'document_translation', label: 'Translation', icon: Languages },
    { key: 'data_export', label: 'Export', icon: Download },
    { key: 'ai_feedback_loop', label: 'AI Feedback', icon: Lightbulb },
  ];

  return (
    <div data-testid="enterprise-intelligence-page" className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)' }}>
        <div className="absolute inset-0 opacity-[0.05]">
          <div className="absolute top-4 right-12 w-48 h-48 border border-white rounded-full" />
          <div className="absolute -bottom-6 left-20 w-32 h-32 border border-cyan-400 rounded-full" />
        </div>
        <div className="relative z-10 p-8">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="h-8 w-8 text-cyan-400" />
            <div>
              <p className="text-xs font-medium text-cyan-400 tracking-wider uppercase">Reason DMS</p>
              <h1 className="text-2xl font-bold text-white">Enterprise Intelligence Engine</h1>
            </div>
          </div>
          <p className="text-slate-300 max-w-2xl text-sm mt-1">
            Advanced document reasoning, governance intelligence, and enterprise automation.
            15 AI-powered components for complete document intelligence.
          </p>
          <div className="flex flex-wrap gap-1.5 mt-4">
            {components.slice(0, 8).map(c => {
              const enabled = config?.config?.components?.[c.key] !== false;
              return (
                <Badge key={c.key} className={`text-xs border-0 ${enabled ? 'bg-cyan-500/20 text-cyan-300' : 'bg-white/5 text-white/40'}`}>
                  {c.label}
                </Badge>
              );
            })}
            <Badge className="bg-white/10 text-white/60 text-xs border-0">
              +{components.length - 8} more
            </Badge>
          </div>
        </div>
      </div>

      {/* Component Status Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
        {components.map(({ key, label, icon: Icon }) => {
          const enabled = config?.config?.components?.[key] !== false;
          return (
            <div key={key} data-testid={`ei-component-${key}`} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-100 hover:shadow-sm transition-shadow">
              <Icon className={`h-3.5 w-3.5 ${enabled ? 'text-cyan-600' : 'text-slate-300'}`} />
              <span className="text-xs font-medium text-slate-700 truncate">{label}</span>
              {enabled ? (
                <CheckCircle className="h-3 w-3 text-emerald-500 shrink-0 ml-auto" />
              ) : (
                <XCircle className="h-3 w-3 text-slate-300 shrink-0 ml-auto" />
              )}
            </div>
          );
        })}
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="analyze" className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger data-testid="tab-analyze" value="analyze" className="flex items-center gap-1.5 text-xs">
            <Brain className="h-3.5 w-3.5" /> Analyze
          </TabsTrigger>
          <TabsTrigger data-testid="tab-compliance" value="compliance" className="flex items-center gap-1.5 text-xs">
            <Shield className="h-3.5 w-3.5" /> Compliance & Risk
          </TabsTrigger>
          <TabsTrigger data-testid="tab-search" value="search" className="flex items-center gap-1.5 text-xs">
            <Search className="h-3.5 w-3.5" /> Search & QA
          </TabsTrigger>
          <TabsTrigger data-testid="tab-tools" value="tools" className="flex items-center gap-1.5 text-xs">
            <Workflow className="h-3.5 w-3.5" /> Tools
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analyze" className="mt-4">
          <IntelligenceAnalyzer />
        </TabsContent>
        <TabsContent value="compliance" className="mt-4">
          <CompliancePanel />
        </TabsContent>
        <TabsContent value="search" className="mt-4">
          <SearchQAPanel />
        </TabsContent>
        <TabsContent value="tools" className="mt-4">
          <ToolsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnterpriseIntelligence;
